import type { ReactNode } from 'react'

interface Column<Row> {
  key: string
  header: string
  align?: 'left' | 'right' | 'center'
  render: (row: Row) => ReactNode
}

interface Props<Row> {
  rows: Row[]
  columns: Column<Row>[]
  emptyMessage?: string
  rowKey: (row: Row) => string
  onRowClick?: (row: Row) => void
  isRowSelected?: (row: Row) => boolean
}

export function DataTable<Row>({
  rows,
  columns,
  emptyMessage = 'No records yet.',
  rowKey,
  onRowClick,
  isRowSelected,
}: Props<Row>) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
        {emptyMessage}
      </div>
    )
  }
  return (
    <div className="overflow-hidden rounded-lg border border-ovation-ink/10 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-ovation-ink/10 bg-ovation-paper text-left text-xs uppercase tracking-wider text-ovation-muted">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = isRowSelected?.(row) ?? false
            return (
              <tr
                key={rowKey(row)}
                className={`border-t border-ovation-ink/5 ${
                  onRowClick ? 'cursor-pointer hover:bg-ovation-ink/5' : ''
                } ${selected ? 'bg-ovation-ink/5' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''}`}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
